import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { ArrowLeft, AlertTriangle, Flag, DollarSign, FileText, CheckCircle, XCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Input } from './ui/input';

const ModeratorDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [pendingReports, setPendingReports] = useState([]);
  const [refundRequests, setRefundRequests] = useState([]);
  const [commentFlags, setCommentFlags] = useState([]);
  const [fraudAlerts, setFraudAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const VALID_TABS = ['pending', 'refunds', 'flags', 'fraud'];
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [moderatorComments, setModeratorComments] = useState('');
  // Pending modal state
  const [selectedPendingReport, setSelectedPendingReport] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // "approve" | "reject"
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [estimatedCost, setEstimatedCost] = useState('');
  const [pendingComments, setPendingComments] = useState('');
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab && VALID_TABS.includes(tab)) {
      setActiveTab(tab);
    }
  }, [location.search]);

  const jumpToTab = (tab) => {
    navigate(`/moderator/dashboard?tab=${tab}`);
  };

  useEffect(() => {
    if (!user || user.role !== 'moderator') {
      navigate('/');
      return;
    }
    fetchDashboardData();
  }, [user, navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, pendingRes, refundsRes, flagsRes, alertsRes] =
        await Promise.all([
          axios.get(`${API}/moderator/dashboard/stats`),
          axios.get(`${API}/moderator/reports/pending`),
          axios.get(`${API}/moderator/refund-requests/pending`),
          axios.get(`${API}/moderator/comment-flags/pending`),
          axios.get(`${API}/moderator/fraud-alerts`),
        ]);
      setStats(statsRes.data);
      setPendingReports(pendingRes.data || []);
      setRefundRequests(refundsRes.data || []);
      setCommentFlags(flagsRes.data || []);
      setFraudAlerts(alertsRes.data || []);
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefundAction = async (action) => {
    try {
      await axios.post(`${API}/moderator/refund-requests/${selectedRefund.id}/process?action=${action}&comments=${encodeURIComponent(moderatorComments)}`);
      toast.success(`Refund ${action}d successfully`);
      setShowRefundModal(false);
      setSelectedRefund(null);
      setModeratorComments('');
      fetchDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const handleCommentFlag = async (flagId, action) => {
    try {
      await axios.post(`${API}/moderator/comment-flags/${flagId}/process?action=${action}`);
      toast.success(`Comment ${action === 'remove_comment' ? 'removed' : 'flag ignored'}`);
      fetchDashboardData();
    } catch (err) {
      toast.error('Action failed');
    }
  };

  const handleFraudAlert = async (alertId, newStatus) => {
    try {
      await axios.post(`${API}/moderator/fraud-alerts/${alertId}/update?new_status=${newStatus}`);
      toast.success('Alert status updated');
      fetchDashboardData();
    } catch (err) {
      toast.error('Update failed');
    }
  };
  // ✅ ADD: pending report approve/reject
  const handlePendingReportAction = async () => {
    if (!selectedPendingReport) return;

    try {
      if (pendingAction === 'approve') {
        const cost = Number(estimatedCost);
        if (!cost || cost < 1000) {
          toast.error('Enter valid estimated cost (min ₹1000)');
          return;
        }

        await axios.post(
          `${API}/moderator/reports/${selectedPendingReport.id}/approve`,
          {
            action: 'approve',
            estimated_cost: cost,
            verification_comments: pendingComments,
          }
        );

        toast.success('Report approved');
      } else {
        if (!pendingComments.trim()) {
          toast.error('Please add rejection reason');
          return;
        }

        await axios.post(
          `${API}/moderator/reports/${selectedPendingReport.id}/approve`,
          {
            action: 'reject',
            verification_comments: pendingComments,
          }
        );

        toast.success('Report rejected');
      }

      setShowPendingModal(false);
      setSelectedPendingReport(null);
      setPendingAction(null);
      setEstimatedCost('');
      setPendingComments('');
      fetchDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-white hover:text-blue-100 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            {t("nav.backHome")}
          </button>
          <h1 className="text-4xl font-bold mb-2">Moderation Dashboard</h1>
          <p className="text-blue-100">Real-time monitoring and action center</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="card p-6 cursor-pointer hover:shadow-lg transition-shadow"onClick={() => jumpToTab('pending')}>
            <div className="flex items-center justify-between mb-2">
              <FileText size={24} className="text-blue-600" />
              <span className="text-3xl font-bold text-gray-900">{stats?.pending_reports ?? 0}</span>
            </div>
            <div className="text-sm text-gray-600">Pending Reports</div>
          </div>
          <div className="card p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => jumpToTab('refunds')}>
            <div className="flex items-center justify-between mb-2">
              <DollarSign size={24} className="text-green-600" />
              <span className="text-3xl font-bold text-gray-900">{stats?.pending_refunds ?? 0}</span>
            </div>
            <div className="text-sm text-gray-600">Refund Requests</div>
          </div>
          <div className="card p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => jumpToTab('flags')}>
            <div className="flex items-center justify-between mb-2">
              <Flag size={24} className="text-orange-600" />
              <span className="text-3xl font-bold text-gray-900">{stats?.pending_flags ?? 0}
              </span>
            </div>
            <div className="text-sm text-gray-600">{t('mod.flaggedComments')}
            </div>
          </div>
          <div className="card p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => jumpToTab('fraud')}>
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle size={24} className="text-red-600" />
              <span className="text-3xl font-bold text-gray-900">{stats?.fraud_alerts ?? 0}</span>
            </div>
            <div className="text-sm text-gray-600">{t('mod.fraudAlerts')}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 pb-12">
        <Tabs value={activeTab} onValueChange={(v) =>navigate(`/moderator/dashboard?tab=${v}`, { replace: true })}className="w-full">
          <TabsList className="grid w-full max-w-3xl grid-cols-4">
            <TabsTrigger value="pending">Pending Reports</TabsTrigger>
            <TabsTrigger value="refunds">Refund Requests</TabsTrigger>
            <TabsTrigger value="flags">{t('mod.flaggedComments')}</TabsTrigger>
            <TabsTrigger value="fraud">{t('mod.fraudAlerts')}</TabsTrigger>
          </TabsList>
          <TabsContent value="pending" className="mt-6">
            {pendingReports.length === 0 ? (
              <div className="card p-12 text-center">
                <FileText size={48} className="mx-auto mb-4 text-gray-400" />
                <p className="text-gray-500">No pending reports</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingReports.map((r) => (
                  <div key={r.id} className="card p-6">
                    <div className="flex items-start justify-between gap-6">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {r.title}
                        </h3>
                        <p className="text-gray-600 mt-1">{r.description}</p>
                        <div className="text-sm text-gray-500 mt-3">
                          <p>Category: {r.category}</p>
                          <p>Urgency: {r.urgency}</p>
                          <p>Location: {r.location?.address}</p>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => {
                            setSelectedPendingReport(r);
                            setPendingAction('approve');
                            setShowPendingModal(true);
                          }}
                        >
                          <CheckCircle size={16} className="mr-1" />
                          Approve
                        </Button>

                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-600 text-red-600 hover:bg-red-50"
                          onClick={() => {
                            setSelectedPendingReport(r);
                            setPendingAction('reject');
                            setShowPendingModal(true);
                          }}
                        >
                          <XCircle size={16} className="mr-1" />
                          Reject
                        </Button>

                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            window.open(
                              `${window.location.origin}/reports/${r.id}`,
                              '_blank',
                              'noopener,noreferrer'
                            )
                          }
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Refund Requests */}
          <TabsContent value="refunds" className="mt-6">
            {refundRequests.length === 0 ? (
              <div className="card p-12 text-center">
                <DollarSign size={48} className="mx-auto mb-4 text-gray-400" />
                <p className="text-gray-500">{t('mod.noRefundRequests')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {refundRequests.map((refund) => (
                  <div key={refund.id} className="card p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xl font-bold text-gray-900">₹{refund.amount.toLocaleString()}</span>
                          <span className="text-sm text-gray-500">{formatDate(refund.created_at)}</span>
                        </div>
                        <p className="text-gray-700 mb-2"><strong>Reason:</strong> {refund.reason}</p>
                        <div className="text-sm text-gray-500">
                          <p>Donation ID: {refund.donation_id}</p>
                          <p>Report ID: {refund.report_id}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            setSelectedRefund(refund);
                            setShowRefundModal(true);
                          }}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle size={16} className="mr-1" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => {
                            setSelectedRefund(refund);
                            setShowRefundModal(true);
                          }}
                          size="sm"
                          variant="outline"
                          className="border-red-600 text-red-600 hover:bg-red-50"
                        >
                          <XCircle size={16} className="mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="flags" className="mt-6">
            {commentFlags.length === 0 ? (
              <div className="card p-12 text-center">
                <Flag size={48} className="mx-auto mb-4 text-gray-400" />
                <p className="text-gray-500">{t("mod.noFlaggedComments")}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {commentFlags.map((flag) => (
                  <div key={flag.id} className="card p-6">
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Flag size={18} className="text-orange-600" />
                        <span className="font-semibold text-gray-900">Flagged by user</span>
                        <span className="text-sm text-gray-500">{formatDate(flag.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2"><strong>Reason:</strong> {flag.reason}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <p className="text-sm text-gray-500 mb-1">Comment by {flag.comment_user}:</p>
                      <p className="text-gray-900">{flag.comment_text}</p>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleCommentFlag(flag.id, 'remove_comment')}
                        size="sm"
                        className="bg-red-600 hover:bg-red-700"
                      >
                        {t("mod.removeComment")}
                      </Button>
                      <Button
                        onClick={() => handleCommentFlag(flag.id, 'ignore')}
                        size="sm"
                        variant="outline"
                      >
                        {t("mod.ignoreFlag")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* {t("mod.fraudAlerts")} */}
          <TabsContent value="fraud" className="mt-6">
            {fraudAlerts.length === 0 ? (
              <div className="card p-12 text-center">
                <AlertTriangle size={48} className="mx-auto mb-4 text-gray-400" />
                <p className="text-gray-500">No active fraud alerts</p>
              </div>
            ) : (
              <div className="space-y-4">
                {fraudAlerts.map((alert) => (
                  <div key={alert.id} className="card p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle size={20} className="text-red-600" />
                          <span className="font-semibold text-gray-900 capitalize">
                            {alert.alert_type.replace('_', ' ')}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            alert.severity === 'high' ? 'bg-red-100 text-red-700' :
                            alert.severity === 'medium' ? 'bg-orange-100 text-orange-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {alert.severity}
                          </span>
                          <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700 capitalize">
                            {alert.status}
                          </span>
                        </div>
                        <p className="text-gray-700 mb-2">{alert.details.message}</p>
                        <div className="text-sm text-gray-500">
                          {alert.user_id && <p>User ID: {alert.user_id}</p>}
                          {alert.report_id && <p>Report ID: {alert.report_id}</p>}
                          <p>{formatDate(alert.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        {alert.status === 'new' && (
                          <Button
                            onClick={() => handleFraudAlert(alert.id, 'investigating')}
                            size="sm"
                            variant="outline"
                          >
                            Investigate
                          </Button>
                        )}
                        <Button
                          onClick={() => handleFraudAlert(alert.id, 'resolved')}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Resolve
                        </Button>
                        <Button
                          onClick={() => handleFraudAlert(alert.id, 'false_positive')}
                          size="sm"
                          variant="outline"
                        >
                          False Positive
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Pending Modal */}
      <Dialog open={showPendingModal} onOpenChange={setShowPendingModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pendingAction === 'approve' ? 'Approve Report' : 'Reject Report'}
            </DialogTitle>
          </DialogHeader>

          {selectedPendingReport && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">
                  Title:{' '}
                  <span className="font-semibold text-gray-900">
                    {selectedPendingReport.title}
                  </span>
                </p>
              </div>

              {pendingAction === 'approve' && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Estimated Cost
                  </label>
                  <Input
                    value={estimatedCost}
                    onChange={(e) => setEstimatedCost(e.target.value)}
                    placeholder="e.g. 5000"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">
                  {pendingAction === 'approve'
                    ? 'Verification Comments (optional)'
                    : 'Rejection Reason *'}
                </label>
                <Textarea
                  value={pendingComments}
                  onChange={(e) => setPendingComments(e.target.value)}
                  rows={3}
                  placeholder="Write here..."
                />
              </div>

              <Button
                onClick={handlePendingReportAction}
                className={
                  pendingAction === 'approve'
                    ? 'bg-green-600 hover:bg-green-700 w-full'
                    : 'bg-red-600 hover:bg-red-700 w-full'
                }
              >
                Confirm
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Refund Modal */}
      <Dialog open={showRefundModal} onOpenChange={setShowRefundModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process Refund Request</DialogTitle>
          </DialogHeader>
          {selectedRefund && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Amount:{' '}<span className="font-bold text-gray-900">₹{selectedRefund.amount}</span></p>
                <p className="text-sm text-gray-600">Reason: {selectedRefund.reason}</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Moderator Comments</label>
                <Textarea
                  placeholder="Add your comments..."
                  value={moderatorComments}
                  onChange={(e) => setModeratorComments(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => handleRefundAction('approve')}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {t("mod.approveRefund")}
                </Button>
                <Button
                  onClick={() => handleRefundAction('reject')}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  {t("mod.rejectRefund")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModeratorDashboard;